import { useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Divider,
  Form as AntForm,
  Input,
  Popconfirm,
  Space,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Laptop, LogOut, Mail, MapPin, Shield, Smartphone, UserCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import type { Translator } from '@/i18n/config';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { Form } from '@shared/components/Form/Form';
import { FormField } from '@shared/components/Form/FormField';
import { ImageField } from '@shared/components/MediaUpload';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { TwoFactorCard } from '../components/TwoFactorCard';
import { NotificationPreferencesCard } from '../components/NotificationPreferencesCard';
import { resolveAssetUrl } from '@shared/utils/assetUrl';
import { useAuth } from '@features/auth/hooks/useAuth';
import { useAuthStore } from '@features/auth/store';
import { useRoleLabel } from '@shared/constants/labels';
import { profileApi } from '@api/profile';
import { recordAuditAction } from '@features/audit/recordAction';
import type { AuthSession } from '../types';

const { Text } = Typography;

const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

function makeInfoSchema(t: Translator) {
  return z.object({
    fullName: z.string().min(2, t('info.validation.fullNameMin')).max(80),
    avatarUrl: z.string().max(500).nullable().optional(),
  });
}
type InfoValues = z.infer<ReturnType<typeof makeInfoSchema>>;

function makePasswordSchema(t: Translator) {
  return z
    .object({
      currentPassword: z.string().min(1, t('password.validation.currentRequired')),
      newPassword: z
        .string()
        .min(8, t('password.validation.newMin'))
        .regex(PASSWORD_RULE, t('password.validation.newPattern')),
      confirm: z.string().min(1, t('validation:passwordConfirmRequired')),
    })
    .refine((data) => data.newPassword === data.confirm, {
      message: t('validation:passwordMismatch'),
      path: ['confirm'],
    });
}
type PasswordValues = z.infer<ReturnType<typeof makePasswordSchema>>;

function initials(name: string | undefined): string {
  if (!name) return 'HD';
  return name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function deviceIcon(s: AuthSession) {
  if (/iphone|ipad|android|mobile/i.test(s.device)) return <Smartphone size={18} />;
  return <Laptop size={18} />;
}

function fmtRelative(iso: string, t: Translator): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return t('sessions.relative.now');
  if (m < 60) return t('sessions.relative.minutes', { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('sessions.relative.hours', { count: h });
  const d = Math.floor(h / 24);
  return t('sessions.relative.days', { count: d });
}

function fmtAbsolute(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('tr-TR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ProfileInfoTab() {
  const { t } = useTranslation(['profile', 'common', 'validation', 'errors']);
  const { user } = useAuth();
  const roleLabel = useRoleLabel();
  const setUser = useAuthStore((s) => s.setUser);

  const infoSchema = useMemo(() => makeInfoSchema(t as unknown as Translator), [t]);

  const mutation = useMutation({
    mutationFn: (values: InfoValues) =>
      // Pass `null` (cleared by user) through to the BE so the avatar is actually
      // removed; only undefined means "leave alone".
      profileApi.update({ fullName: values.fullName, avatarUrl: values.avatarUrl ?? null }),
    onSuccess: (updated) => {
      // Yalnız profil alanları değişti; token/proje bağlamı aynı kalır.
      setUser(updated);
      toast.success(t('info.toast.success'));
      void recordAuditAction({
        action: 'profile.update',
        resource: 'profile',
        resourceId: updated.id,
        summary: t('info.audit.summary'),
      });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  if (!user) return null;

  return (
    <Card>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Avatar
          size={96}
          src={resolveAssetUrl(user.avatarUrl) ?? undefined}
          style={{
            background: 'var(--ds-brand)',
            color: 'var(--ds-text-on-brand)',
            fontSize: 32,
            fontWeight: 600,
          }}
        >
          {initials(user.fullName)}
        </Avatar>
        <div style={{ flex: '1 1 320px', minWidth: 280 }}>
          <Form
            schema={infoSchema}
            defaultValues={{ fullName: user.fullName, avatarUrl: user.avatarUrl ?? '' }}
            onSubmit={(values) => mutation.mutate(values)}
          >
            <AntForm layout="vertical" component="div">
              <FormField name="fullName" label={t('info.fields.fullName.label')} required>
                <Input size="large" placeholder={t('info.fields.fullName.placeholder')} prefix={<UserCircle2 size={14} />} />
              </FormField>
              <FormField name="avatarUrl" label={t('info.fields.avatar.label')} help={t('info.fields.avatar.help')}>
                <ImageField label={t('info.fields.avatar.imageLabel')} maxSizeMB={2} hint={t('info.fields.avatar.hint')} />
              </FormField>
              <AntForm.Item label={t('info.fields.email.label')}>
                <Input value={user.email} disabled size="large" prefix={<Mail size={14} />} />
              </AntForm.Item>
              <AntForm.Item label={t('info.fields.role.label')}>
                <Tag color="blue" style={{ fontSize: 13, padding: '4px 10px' }}>
                  <Shield size={12} style={{ marginRight: 4, verticalAlign: -2 }} />
                  {roleLabel(user.role)}
                </Tag>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  {t('info.fields.role.changeHint')}
                </Text>
              </AntForm.Item>
            </AntForm>
            <Button type="primary" htmlType="submit" loading={mutation.isPending}>
              {t('info.submit')}
            </Button>
          </Form>
        </div>
      </div>
    </Card>
  );
}

function PasswordTab() {
  const { t } = useTranslation(['profile', 'common', 'validation', 'errors']);
  const [serverError, setServerError] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);
  const setSession = useAuthStore((s) => s.setSession);
  const queryClient = useQueryClient();

  const passwordSchema = useMemo(() => makePasswordSchema(t as unknown as Translator), [t]);

  const mutation = useMutation({
    mutationFn: (values: PasswordValues) =>
      profileApi.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      }),
    onSuccess: (session) => {
      // BE revoked the old session and minted a fresh one. Swap the store over
      // to the new tokens (and the cleared mustResetPassword flag) so the user
      // stays signed in — no forced logout/login after a password change.
      setSession(session);
      // If we were in the forced-reset state, screens loaded earlier got
      // 403 PASSWORD_RESET_REQUIRED. Refetch everything now that the gate is
      // cleared so data appears without a manual reload.
      void queryClient.invalidateQueries();
      toast.success(t('password.toast.success'));
      setFormKey((k) => k + 1);
      void recordAuditAction({
        action: 'auth.password.change',
        resource: 'auth',
        summary: t('password.audit.summary'),
      });
    },
    onError: (err) => {
      setServerError(resolveApiErrorMessage(err));
    },
  });

  return (
    <Card>
      {serverError ? (
        <Alert
          type="error"
          message={serverError}
          showIcon
          closable
          onClose={() => setServerError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}
      <Form
        key={formKey}
        schema={passwordSchema}
        defaultValues={{ currentPassword: '', newPassword: '', confirm: '' }}
        onSubmit={(values) => mutation.mutate(values)}
      >
        <AntForm layout="vertical" component="div">
          <FormField name="currentPassword" label={t('password.fields.current.label')} required>
            <Input.Password size="large" autoComplete="current-password" />
          </FormField>
          <FormField name="newPassword" label={t('password.fields.new.label')} required help={t('password.fields.new.help')}>
            <Input.Password size="large" autoComplete="new-password" />
          </FormField>
          <FormField name="confirm" label={t('password.fields.confirm.label')} required>
            <Input.Password size="large" autoComplete="new-password" />
          </FormField>
        </AntForm>
        <Button type="primary" htmlType="submit" loading={mutation.isPending}>
          {t('password.submit')}
        </Button>
      </Form>
    </Card>
  );
}

function SessionsTab() {
  const { t } = useTranslation(['profile', 'common', 'validation', 'errors']);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['profile', 'sessions'],
    queryFn: () => profileApi.listSessions(),
  });

  const sessions = data ?? [];
  const current = sessions.find((s) => s.current);
  const others = sessions
    .filter((s) => !s.current)
    .sort((a, b) => new Date(b.lastActiveAt).getTime() - new Date(a.lastActiveAt).getTime());

  const revoke = useMutation({
    mutationFn: (id: string) => profileApi.revokeSession(id),
    onSuccess: async (_d, id) => {
      // Optimistically drop the revoked session so the row disappears even if
      // the background refetch is delayed. The chain-revoke on the BE may also
      // remove sibling tokens; the follow-up refetch reconciles those.
      qc.setQueryData<AuthSession[] | undefined>(['profile', 'sessions'], (prev) =>
        prev ? prev.filter((s) => s.id !== id) : prev,
      );
      toast.success(t('sessions.toast.revoked'));
      void recordAuditAction({
        action: 'auth.session.revoke',
        resource: 'session',
        resourceId: id,
        summary: t('sessions.audit.summary', { id }),
      });
      await qc.invalidateQueries({ queryKey: ['profile', 'sessions'], refetchType: 'all' });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  // No bulk endpoint on the BE; revoke each non-current session individually and
  // reconcile once via a single refetch afterwards.
  const revokeAll = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => profileApi.revokeSession(id)));
      return ids;
    },
    onSuccess: async (ids) => {
      qc.setQueryData<AuthSession[] | undefined>(['profile', 'sessions'], (prev) =>
        prev ? prev.filter((s) => s.current) : prev,
      );
      toast.success(t('sessions.toast.revokedAll'));
      void recordAuditAction({
        action: 'auth.session.revoke_all',
        resource: 'session',
        summary: t('sessions.audit.revokeAllSummary', { count: ids.length }),
      });
      await qc.invalidateQueries({ queryKey: ['profile', 'sessions'], refetchType: 'all' });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const busy = revoke.isPending || revokeAll.isPending;

  const renderRow = (s: AuthSession, hero: boolean) => (
    <div
      key={s.id}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: 16,
        border: hero ? '1px solid var(--ds-brand)' : '1px solid var(--ds-border)',
        background: hero ? 'var(--ds-surface-2)' : undefined,
        borderRadius: 12,
        flexWrap: 'wrap',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: hero ? 'var(--ds-brand)' : 'var(--ds-surface-2)',
          color: hero ? 'var(--ds-text-on-brand)' : undefined,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {deviceIcon(s)}
      </div>
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          {s.device} · {s.browser}
          {s.current ? <Tag color="green">{t('sessions.activeNow')}</Tag> : null}
        </div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          <MapPin size={11} style={{ verticalAlign: -1 }} /> {s.location ?? s.ip} ·{' '}
          <Tooltip title={t('sessions.lastActiveTooltip', { absolute: fmtAbsolute(s.lastActiveAt) })}>
            <span>
              {t('sessions.lastActive', {
                relative: fmtRelative(s.lastActiveAt, t as unknown as Translator),
              })}
            </span>
          </Tooltip>
        </Text>
      </div>
      <div>
        {s.current ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('sessions.currentHint')}
          </Text>
        ) : (
          <Popconfirm
            title={t('sessions.revoke.confirmTitle')}
            description={t('sessions.revoke.confirmDescription')}
            okText={t('sessions.revoke.okText')}
            okButtonProps={{ danger: true }}
            cancelText={t('common:actions.cancel')}
            onConfirm={() => revoke.mutate(s.id)}
          >
            <Button
              danger
              icon={<LogOut size={14} />}
              loading={revoke.isPending && revoke.variables === s.id}
              disabled={busy}
            >
              {t('sessions.revoke.button')}
            </Button>
          </Popconfirm>
        )}
      </div>
    </div>
  );

  return (
    <Card
      title={t('sessions.title')}
      extra={
        <Text type="secondary">{t('sessions.openCount', { count: sessions.length })}</Text>
      }
      loading={isLoading}
    >
      <Alert
        type="info"
        showIcon
        message={t('sessions.intro')}
        style={{ marginBottom: 16 }}
      />

      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {current ? (
          <>
            <Text strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--ds-text-secondary)' }}>
              {t('sessions.thisDevice')}
            </Text>
            {renderRow(current, true)}
          </>
        ) : null}

        <Divider style={{ margin: '8px 0' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {others.length > 0
              ? t('sessions.otherCount', { count: others.length })
              : t('sessions.otherSessions')}
          </Text>
        </Divider>

        {others.length > 0 ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Popconfirm
                title={t('sessions.revokeAll.confirmTitle')}
                description={t('sessions.revokeAll.confirmDescription')}
                okText={t('sessions.revokeAll.okText')}
                okButtonProps={{ danger: true }}
                cancelText={t('common:actions.cancel')}
                onConfirm={() => revokeAll.mutate(others.map((s) => s.id))}
              >
                <Button danger icon={<LogOut size={14} />} loading={revokeAll.isPending} disabled={busy}>
                  {t('sessions.revokeAll.button')}
                </Button>
              </Popconfirm>
            </div>
            {others.map((s) => renderRow(s, false))}
          </>
        ) : (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t('sessions.noOthers')}
          </Text>
        )}
      </Space>
    </Card>
  );
}

export function ProfilePage() {
  const { t } = useTranslation(['profile', 'common']);
  // Tab is mirrored into `?tab=` so the forced-password-reset gate can deep-link
  // straight to the password tab (`/profile?tab=password`).
  const [params, setParams] = useSearchParams();
  const activeTab = params.get('tab') ?? 'info';
  return (
    <div>
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        breadcrumb={[{ label: t('common:labels.home'), to: '/' }, { label: t('page.title') }]}
      />
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setParams(key === 'info' ? {} : { tab: key }, { replace: true })}
        items={[
          { key: 'info', label: t('tabs.info'), children: <ProfileInfoTab /> },
          { key: 'password', label: t('tabs.password'), children: <PasswordTab /> },
          { key: 'security', label: t('tabs.security'), children: <TwoFactorCard /> },
          {
            key: 'notifications',
            label: t('tabs.notifications'),
            children: <NotificationPreferencesCard />,
          },
          { key: 'sessions', label: t('tabs.sessions'), children: <SessionsTab /> },
        ]}
      />
    </div>
  );
}
